import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { STATUS, ACTION_STATUS, StatusBadge } from "@/lib/status";
import ActionPlanner from "@/pages/admin/ActionPlanner";
import { Copy, Download, KeyRound, RefreshCw, Mail, Send } from "lucide-react";
import { PhotoStrip } from "@/components/Photos";
import { ProposalDecision } from "@/components/ServiceReport";
import HouseEditor from "@/pages/admin/HouseEditor";
import SVJSettings from "@/pages/admin/SVJSettings";
import Protocols from "@/pages/admin/Protocols";
import { Checkbox } from "@/components/ui/checkbox";
import { OccupancyCalendar } from "@/components/OccupancyCalendar";

export default function SVJWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [svj, setSvj] = useState(null);
  const [actions, setActions] = useState([]);
  const [actionId, setActionId] = useState(null);
  const action = useMemo(() => actions.find((a) => a.id === actionId), [actions, actionId]);
  const [tab, setTab] = useState("prehled");
  const [schedDate, setSchedDate] = useState("");
  const openDay = (iso) => { setSchedDate(iso); setTab("harmonogram"); };

  const loadSvj = useCallback(() => api.get(`/svjs/${id}`).then((r) => setSvj(r.data)).catch((e) => toast.error(apiErr(e))), [id]);
  const loadActions = useCallback(() => api.get(`/svjs/${id}/actions`).then((r) => {
    setActions(r.data);
    setActionId((cur) => cur || (r.data[0]?.id ?? null));
  }), [id]);
  useEffect(() => { loadSvj(); loadActions(); }, [loadSvj, loadActions]);

  if (!svj) return <p className="text-slate-500">Načítání…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2540] break-words">{svj.name}</h1>
          <p className="text-slate-500 text-sm sm:text-base">{svj.address}</p>
        </div>
        {actions.length > 0 && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Label className="text-sm text-slate-500 shrink-0">Akce:</Label>
            <Select value={actionId || ""} onValueChange={setActionId}>
              <SelectTrigger className="w-full sm:w-64" data-testid="action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {actions.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {action && <span className={`text-xs px-2 py-1 rounded border shrink-0 ${ACTION_STATUS[action.status]?.cls}`}>{ACTION_STATUS[action.status]?.label}</span>}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
          <TabsList className="h-auto inline-flex w-max sm:flex sm:flex-wrap sm:w-auto">
          <TabsTrigger value="prehled" data-testid="tab-prehled">Přehled</TabsTrigger>
          <TabsTrigger value="dum" data-testid="tab-dum">Dům</TabsTrigger>
          <TabsTrigger value="struktura" data-testid="tab-struktura">Struktura domu</TabsTrigger>
          <TabsTrigger value="harmonogram" data-testid="tab-harmonogram">Harmonogram</TabsTrigger>
          <TabsTrigger value="rezervace" data-testid="tab-rezervace">Rezervace</TabsTrigger>
          <TabsTrigger value="protokoly" data-testid="tab-protokoly">Protokoly</TabsTrigger>
          <TabsTrigger value="akce" data-testid="tab-akce">Akce &amp; plán</TabsTrigger>
          <TabsTrigger value="pristup" data-testid="tab-pristup">Přístup obyvatel</TabsTrigger>
          <TabsTrigger value="ozn" data-testid="tab-ozn">Oznámení</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
          <TabsTrigger value="nastaveni" data-testid="tab-nastaveni">Nastavení &amp; GDPR</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="prehled"><Overview svjId={id} actionId={actionId} onDay={openDay} svj={svj} /></TabsContent>
        <TabsContent value="dum"><HouseGrid actionId={actionId} /></TabsContent>
        <TabsContent value="struktura"><HouseEditor svjId={id} onChange={loadSvj} /></TabsContent>
        <TabsContent value="harmonogram"><Schedule actionId={actionId} onChange={loadActions} svj={svj} action={action} date={schedDate} setDate={setSchedDate} /></TabsContent>
        <TabsContent value="rezervace"><Reservations actionId={actionId} svj={svj} onChange={loadActions} /></TabsContent>
        <TabsContent value="protokoly"><Protocols svjId={id} actionId={actionId} actionName={action?.name} /></TabsContent>
        <TabsContent value="akce"><ActionPlanner svj={svj} actions={actions} actionId={actionId} setActionId={setActionId} reload={loadActions} isSuper={isSuper} /></TabsContent>
        <TabsContent value="pristup"><ResidentAccess svjId={id} actions={actions} isSuper={isSuper} svj={svj} currentActionId={actionId} /></TabsContent>
        <TabsContent value="ozn"><Notifications svjId={id} /></TabsContent>
        <TabsContent value="audit"><Audit svjId={id} /></TabsContent>
        <TabsContent value="nastaveni"><SVJSettings svj={svj} isSuper={isSuper} reload={loadSvj} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, color }) {
  return <div className="bg-white rounded-xl border p-3 sm:p-5"><div className="text-2xl sm:text-3xl font-bold" style={{ color }}>{value}</div><div className="text-slate-500 text-xs sm:text-sm mt-1">{label}</div></div>;
}

function Overview({ svjId, actionId, onDay, svj }) {
  const [data, setData] = useState(null);
  const [addDate, setAddDate] = useState(null);
  const load = () => { if (actionId) api.get(`/svjs/${svjId}/overview?action_id=${actionId}`).then((r) => setData(r.data)); };
  useEffect(() => { load(); }, [svjId, actionId]);
  if (!actionId) return <Empty text="Zatím není žádná servisní akce. Vytvořte ji v záložce Akce & plán." />;
  if (!data) return <p className="text-slate-500 mt-4">Načítání…</p>;
  const s = data.stats;
  return (
    <div className="space-y-6 mt-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        <Stat label="Zahrnuté byty" value={s.included} color="#0A2540" />
        <Stat label="Rezervováno" value={s.reserved} color="#1E40AF" />
        <Stat label="Bez rezervace" value={s.unbooked} color="#64748B" />
        <Stat label="Hotovo" value={s.done} color="#065F46" />
        <Stat label="Nezastiženo" value={s.missed} color="#991B1B" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5" data-testid="occupancy-card">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-semibold text-[#0A2540]">Obsazenost termínů</h3>
            <span className="text-sm text-slate-500" data-testid="occupancy-unbooked">Bytů bez rezervace: <b className="text-[#0A2540]">{s.unbooked}</b></span>
          </div>
          <OccupancyCalendar days={data.days || []} adminOnlyFrom={data.admin_only_from} onDay={(iso) => setAddDate(iso)} />
          <p className="text-xs text-slate-500 mt-2">Klepnutím na den rovnou zapíšete byt na volný termín. <button className="underline decoration-dotted" onClick={() => onDay?.("")} data-testid="occ-open-schedule">Otevřít harmonogram</button></p>
          {addDate && <AddResDialog actionId={actionId} svj={svj} preDate={addDate} onClose={() => setAddDate(null)} onDone={() => { setAddDate(null); load(); }} />}
        </div>
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-[#0A2540] mb-3">Zaplnění po vchodech</h3>
          <div className="space-y-3">
            {data.entrances_fill.map((e) => (
              <div key={e.entrance}>
                <div className="flex justify-between text-sm mb-1"><span>Vchod {e.entrance}</span><span>{e.reserved}/{e.total} · bez rezervace {e.total - e.reserved}</span></div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${e.total ? (e.reserved / e.total) * 100 : 0}%`, background: "#C5A059" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HouseGrid({ actionId }) {
  const [data, setData] = useState(null);
  const load = () => actionId && api.get(`/actions/${actionId}/house`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [actionId]);
  if (!actionId) return <Empty text="Vyberte servisní akci." />;
  if (!data) return <p className="text-slate-500 mt-4">Načítání…</p>;
  return (
    <div className="mt-4 space-y-6">
      <Legend />
      {data.entrances.map((e) => (
        <div key={e.entrance_id} className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-[#0A2540] mb-3">Vchod {e.label}</h3>
          <div className="space-y-2">
            {e.floors.map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <div className="w-16 text-xs text-slate-500 shrink-0">Patro {f.label}</div>
                <div className="flex gap-2 flex-wrap">
                  {f.apartments.map((a) => {
                    const st = STATUS[a.status] || STATUS.bez_rezervace;
                    return (
                      <div key={a.id} data-testid={`grid-apt-${a.number}`} title={`${st.label}${a.owner_name ? ` · ${a.owner_name}` : ""}`}
                        className="w-24 rounded-lg border-2 p-2 text-center" style={{ background: st.bg, borderColor: st.border }}>
                        <div className="font-bold text-sm" style={{ color: st.text }}>{a.number}</div>
                        {a.owner_name && <div className="text-[9px] leading-tight truncate" style={{ color: st.text }}>{a.owner_name}</div>}
                        <div className="text-[10px]" style={{ color: st.text }}>{st.label}</div>
                        {a.start && <div className="text-[10px]" style={{ color: st.text }}>{a.date?.slice(5)} {a.start}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex gap-3 flex-wrap">
      {["bez_rezervace", "scheduled", "done", "missed"].map((k) => (
        <div key={k} className="flex items-center gap-1.5 text-sm">
          <span className="w-4 h-4 rounded border-2" style={{ background: STATUS[k].bg, borderColor: STATUS[k].border }} />
          {STATUS[k].label}
        </div>
      ))}
    </div>
  );
}

function Schedule({ actionId, svj, action, date, setDate }) {
  const [rows, setRows] = useState([]);
  const load = () => {
    if (!actionId) return;
    const q = date ? `?date_from=${date}` : "";
    api.get(`/actions/${actionId}/schedule${q}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [actionId, date]);
  if (!actionId) return <Empty text="Vyberte servisní akci." />;
  const dl = (kind) => window.open(`${API}/actions/${actionId}/export/${kind}?token_hint=1`, "_blank");
  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border p-4">
        <div className="space-y-1"><Label className="text-xs">Filtr dne</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="schedule-date" /></div>
        {date && <Button variant="ghost" size="sm" onClick={() => setDate("")}>Zrušit filtr</Button>}
        <div className="flex-1" />
        <ExportButtons actionId={actionId} kinds={[["schedule.csv","CSV"],["schedule.pdf","PDF"],["board.pdf","Nástěnka (bez kontaktů)"],["flyer.pdf","Leták + QR"],["poster.pdf","Plakát A4 velký QR"],["protocol.pdf","Protokol servisu (PDF)"]]} />
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr>
            {["Datum","Čas","Tým","Vchod","Patro","Byt","Kontakt","Stav"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`schedule-row-${r.id}`}>
                <td className="px-3 py-2">{r.date}</td><td className="px-3 py-2">{r.start}–{r.end}</td>
                <td className="px-3 py-2">{r.team || "—"}</td><td className="px-3 py-2">{r.entrance}</td>
                <td className="px-3 py-2">{r.floor}</td><td className="px-3 py-2 font-semibold">{r.apartment}</td>
                <td className="px-3 py-2">{r.phone}<div className="text-xs text-slate-400">{r.email}</div></td>
                <td className="px-3 py-2"><StatusBadge status={r.service_status} />{r.tech_note && <div className="text-xs text-slate-600 mt-1 max-w-56 whitespace-pre-line" data-testid={`sched-tech-note-${r.id}`}><span className="text-[#C5A059] font-medium">Technik:</span> {r.tech_note}</div>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="8" className="px-3 py-6 text-center text-slate-500">Žádné rezervace.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExportButtons({ actionId, kinds }) {
  const dl = async (kind) => {
    try {
      const res = await api.get(`/actions/${actionId}/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = kind.replace(".", "_").replace("_pdf", ".pdf").replace("_csv", ".csv"); a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return <div className="flex gap-2 flex-wrap">{kinds.map(([k, label]) => (
    <Button key={k} size="sm" variant="outline" onClick={() => dl(k)} data-testid={`export-${k}`}><Download className="h-4 w-4 mr-1" />{label}</Button>
  ))}</div>;
}

function Reservations({ actionId, svj, onChange }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [move, setMove] = useState(null);
  const [add, setAdd] = useState(false);
  const load = () => {
    if (!actionId) return;
    let url = `/actions/${actionId}/reservations?`;
    if (statusF) url += `status=${statusF}&`;
    if (q) url += `q=${encodeURIComponent(q)}`;
    api.get(url).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [actionId, statusF, q]);
  if (!actionId) return <Empty text="Vyberte servisní akci." />;

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); onChange?.(); } catch (e) { toast.error(apiErr(e)); } };
  const doCancel = (r) => { const reason = window.prompt("Důvod zrušení rezervace:"); if (reason) act(() => api.post(`/reservations/${r.id}/cancel`, { reason }), "Rezervace zrušena"); };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-3 items-end bg-white rounded-xl border p-4">
        <Input placeholder="Hledat byt, jméno, telefon…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:w-64" data-testid="res-search" />
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="res-status-filter"><SelectValue placeholder="Stav" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všechny stavy</SelectItem>
            <SelectItem value="scheduled">Rezervováno</SelectItem>
            <SelectItem value="done">Hotovo</SelectItem>
            <SelectItem value="missed">Nezastižen</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setAdd(true)} style={{ background: "#0A2540" }} data-testid="add-reservation-btn">Přidat rezervaci</Button>
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr>
            {["Byt","Termín","Kontakt","Zdroj","Stav","Akce"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`res-row-${r.id}`}>
                <td className="px-3 py-2 font-semibold">V{r.entrance}/{r.apartment}<div className="text-xs text-slate-400">patro {r.floor} · {r.code}</div></td>
                <td className="px-3 py-2">{r.date} {r.start}–{r.end}</td>
                <td className="px-3 py-2">{r.name || "—"}<div className="text-xs text-slate-400">{r.phone} {r.email_missing && <span className="text-amber-600">• chybí e-mail</span>}</div></td>
                <td className="px-3 py-2 text-xs">{r.source === "public" ? "Formulář" : "Ručně"}</td>
                <td className="px-3 py-2"><StatusBadge status={r.service_status} />{r.tech_note && <div className="text-xs text-slate-600 mt-1 max-w-56 whitespace-pre-line" title={`Technik ${r.tech_note_by || ""}`} data-testid={`res-tech-note-${r.id}`}><span className="text-[#C5A059] font-medium">Technik:</span> {r.tech_note}</div>}{r.photos?.length > 0 && <div className="mt-1"><PhotoStrip photos={r.photos} canDelete onDeleted={load} testPrefix={`res-photo-${r.id}`} /></div>}{r.report && <div className="mt-1 max-w-72"><ProposalDecision r={r} onDone={load} /></div>}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setMove(r)} data-testid={`move-${r.id}`}>Přesunout</Button>
                    <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/reservations/${r.id}/done`), "Označeno hotovo")}>Hotovo</Button>
                    <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/reservations/${r.id}/missed`), "Nezastižen")}>Nezastižen</Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => doCancel(r)} data-testid={`cancel-${r.id}`}>Zrušit</Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="px-3 py-6 text-center text-slate-500">Žádné rezervace.</td></tr>}
          </tbody>
        </table>
      </div>
      {move && <MoveDialog res={move} onClose={() => setMove(null)} onDone={() => { setMove(null); load(); }} />}
      {add && <AddResDialog actionId={actionId} svj={svj} onClose={() => setAdd(false)} onDone={() => { setAdd(false); load(); onChange?.(); }} />}
    </div>
  );
}

function MoveDialog({ res, onClose, onDone }) {
  const [days, setDays] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [anyEnt, setAnyEnt] = useState(false);
  useEffect(() => {
    api.get(`/actions/${res.action_id}/free-slots${anyEnt ? "" : `?entrance_id=${res.entrance_id}`}`)
      .then((r) => setDays(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [res.id, anyEnt]);
  const submit = async () => {
    try { await api.post(`/reservations/${res.id}/move`, { slot_id: slotId }); toast.success("Termín přesunut"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle>Přesunout rezervaci · byt {res.apartment}</DialogTitle></DialogHeader>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={anyEnt} onCheckedChange={(c) => { setAnyEnt(!!c); setSlotId(""); }} data-testid="move-any-entrance" /> Zobrazit termíny všech vchodů (předseda může rezervovat kamkoliv)</label>
        <div className="space-y-3 max-h-80 overflow-auto">
          {days.length === 0 && <p className="text-slate-500 text-sm">Žádné volné sloty{anyEnt ? "" : " ve vlastním vchodu"}.</p>}
          {days.map((d) => (
            <div key={d.date}><div className="text-xs text-slate-500 mb-1">{d.date}</div>
              <div className="flex gap-2 flex-wrap">{d.slots.map((s) => (
                <button key={s.id} onClick={() => setSlotId(s.id)} className={`px-3 py-1.5 rounded border text-sm ${slotId === s.id ? "bg-[#0A2540] text-white" : "bg-white"}`}>{s.start}{anyEnt && <span className="text-[10px] opacity-70"> V{s.entrance}</span>}</button>
              ))}</div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Zrušit</Button><Button onClick={submit} disabled={!slotId} style={{ background: "#0A2540" }} data-testid="confirm-move">Přesunout</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddResDialog({ actionId, svj, onClose, onDone, preDate }) {
  const [entrances, setEntrances] = useState([]);
  const [aptId, setAptId] = useState("");
  const [entId, setEntId] = useState("");
  const [days, setDays] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [f, setF] = useState({ phone: "", email: "", name: "", note: "" });
  const [anyEnt, setAnyEnt] = useState(!!preDate);
  useEffect(() => { api.get(`/actions/${actionId}/house`).then((r) => setEntrances(r.data.entrances)); }, [actionId]);
  useEffect(() => {
    if (!entId && !preDate) return;
    api.get(`/actions/${actionId}/free-slots${anyEnt || !entId ? "" : `?entrance_id=${entId}`}`)
      .then((r) => setDays(preDate ? r.data.filter((d) => d.date === preDate) : r.data)).catch(() => setDays([]));
  }, [entId, anyEnt, preDate]);
  const apts = entrances.find((e) => e.entrance_id === entId)?.floors.flatMap((fl) => fl.apartments) || [];
  const submit = async () => {
    try {
      await api.post(`/actions/${actionId}/reservations`, { apartment_id: aptId, slot_id: slotId, phone: f.phone, email: f.email || null, name: f.name, note: f.note });
      toast.success("Rezervace přidána"); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{preDate ? `Zapsat byt na ${preDate.split("-").reverse().map(Number).join(". ")}` : "Přidat rezervaci za obyvatele"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {preDate && (
            <div className="space-y-1" data-testid="cal-add-slots">
              <div className="text-xs text-slate-500">1. Volné časy tohoto dne (všechny vchody)</div>
              {days.length === 0 && <p className="text-xs text-slate-500">Žádné volné termíny v tento den.</p>}
              <div className="flex gap-2 flex-wrap">{days.flatMap((d) => d.slots).map((s) => <button key={s.id} onClick={() => setSlotId(s.id)} data-testid={`cal-add-slot-${s.start}-${s.entrance}`} className={`px-3 py-1.5 rounded border text-sm ${slotId === s.id ? "bg-[#0A2540] text-white" : "bg-white"}`}>{s.start}–{s.end}<span className="text-[10px] opacity-70"> V{s.entrance}</span></button>)}</div>
              <div className="text-xs text-slate-500 pt-2">2. Byt</div>
            </div>
          )}
          <Select value={entId} onValueChange={(v) => { setEntId(v); setAptId(""); if (!preDate) setSlotId(""); }}>
            <SelectTrigger data-testid="add-entrance"><SelectValue placeholder="Vchod" /></SelectTrigger>
            <SelectContent>{entrances.map((e) => <SelectItem key={e.entrance_id} value={e.entrance_id}>Vchod {e.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={aptId} onValueChange={setAptId}>
            <SelectTrigger data-testid="add-apt"><SelectValue placeholder="Byt" /></SelectTrigger>
            <SelectContent>{apts.filter((a) => a.status === "bez_rezervace").map((a) => <SelectItem key={a.id} value={a.id}>Byt {a.number}{a.owner_name ? ` (${a.owner_name})` : ""}</SelectItem>)}</SelectContent>
          </Select>
          {!preDate && <label className="flex items-center gap-2 text-sm"><Checkbox checked={anyEnt} onCheckedChange={(c) => { setAnyEnt(!!c); setSlotId(""); }} data-testid="add-any-entrance" /> Termíny všech vchodů (rezervace kamkoliv)</label>}
          {!preDate && <div className="max-h-40 overflow-auto space-y-2">
            {entId && days.length === 0 && <p className="text-xs text-slate-500">Žádné volné termíny{anyEnt ? "" : " v tomto vchodu – zkuste zapnout termíny všech vchodů"}.</p>}
            {days.map((d) => (<div key={d.date}><div className="text-xs text-slate-500">{d.date}</div>
              <div className="flex gap-2 flex-wrap">{d.slots.map((s) => <button key={s.id} onClick={() => setSlotId(s.id)} className={`px-3 py-1 rounded border text-sm ${slotId === s.id ? "bg-[#0A2540] text-white" : ""}`}>{s.start}{anyEnt && <span className="text-[10px] opacity-70"> V{s.entrance}</span>}</button>)}</div></div>))}
          </div>}
          {preDate && <div className="text-xs text-slate-500">3. Kontakt na obyvatele</div>}
          <Input placeholder="Telefon *" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="add-phone" />
          <Input placeholder="E-mail *" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-email" />
          <Input placeholder="Jméno na zvonku *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-name" />
          <Textarea placeholder="Poznámka" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Zrušit</Button><Button onClick={submit} disabled={!aptId || !slotId || !f.phone || !f.email.includes("@") || f.name.trim().length < 2} style={{ background: "#0A2540" }} data-testid="confirm-add-res">Uložit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResidentAccess({ svjId, actions, isSuper, svj, currentActionId }) {
  const [acc, setAcc] = useState(null);
  const [gen, setGen] = useState(null);
  const [actionId, setActionId] = useState("");
  const [flyer, setFlyer] = useState(false);
  const [remind, setRemind] = useState(false);
  const inviteChairman = async () => {
    try { const { data } = await api.post(`/svjs/${svjId}/invite-chairman`); toast.success(`Pozvánka odeslána na ${data.sent_to}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const load = () => api.get(`/svjs/${svjId}/resident-access`).then((r) => setAcc(r.data));
  useEffect(() => { load(); }, [svjId]);
  useEffect(() => { if (actions[0]) setActionId(actions[0].id); }, [actions]);
  const rotate = async () => {
    try { const { data } = await api.post(`/svjs/${svjId}/resident-access`, { action_id: actionId }); setGen(data); load(); toast.success("Vstup vytvořen / obnoven"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-4 max-w-xl space-y-4">
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><KeyRound className="h-4 w-4" /> Společný vstup obyvatel</h3>
        {acc?.exists ? (
          <>
            <div className="bg-slate-50 border rounded-lg p-3 flex items-center gap-2">
              <code className="text-sm flex-1 break-all" data-testid="resident-link">{acc.link}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(acc.link); toast.success("Odkaz zkopírován"); }}><Copy className="h-4 w-4" /></Button>
            </div>
            {acc.code
              ? <p className="text-sm">Přístupový kód: <b className="okenfix-brand text-lg" style={{ color: "#C5A059" }} data-testid="access-code">{acc.code}</b></p>
              : <p className="text-sm text-slate-500">Kód byl vytvořen ve starší verzi – pro zobrazení ho obnovte.</p>}
          </>
        ) : <p className="text-slate-500">Zatím nevytvořeno.</p>}
        {isSuper && (
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1"><Label className="text-xs">Aktuální akce</Label>
              <Select value={actionId} onValueChange={setActionId}><SelectTrigger data-testid="ra-action"><SelectValue /></SelectTrigger>
                <SelectContent>{actions.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
            <Button onClick={rotate} style={{ background: "#0A2540" }} data-testid="rotate-access"><RefreshCw className="h-4 w-4 mr-1" /> {acc?.exists ? "Obnovit kód" : "Vytvořit"}</Button>
          </div>
        )}
        {gen && (
          <div className="bg-[#FDF8EC] border border-[#C5A059] rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-1">Předejte obyvatelům:</p>
            <p className="break-all"><b>Odkaz:</b> {gen.link}</p>
            <p className="text-lg"><b>Kód:</b> <span className="okenfix-brand" style={{ color: "#C5A059" }} data-testid="generated-code">{gen.code}</span></p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-3">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><Mail className="h-4 w-4" /> Hromadné pozvánky</h3>
        <p className="text-sm text-slate-500">Rozešlete obyvatelům e-mailem leták s odkazem, přístupovým kódem a QR kódem. Předsedovi lze znovu poslat pozvánku k aktivaci účtu.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setFlyer(true)} disabled={!acc?.exists} style={{ background: "#0A2540" }} data-testid="send-flyer-btn"><Send className="h-4 w-4 mr-1" /> Rozeslat leták obyvatelům</Button>
          <Button onClick={() => setRemind(true)} disabled={!acc?.exists || !currentActionId} variant="outline" data-testid="remind-unbooked-btn"><Send className="h-4 w-4 mr-1" /> Připomínka nerezervovaným</Button>
          {isSuper && <Button variant="outline" onClick={inviteChairman} data-testid="invite-chairman-btn"><Mail className="h-4 w-4 mr-1" /> Poslat pozvánku předsedovi ({svj?.chairman_email})</Button>}
        </div>
        {!acc?.exists && <p className="text-xs text-amber-600">Nejdříve vytvořte společný vstup obyvatel.</p>}
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-3" data-testid="print-materials">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><Download className="h-4 w-4" /> Tiskoviny pro nástěnku</h3>
        <p className="text-sm text-slate-500">Vytiskněte na A4 a vyvěste na nástěnku SVJ. Plakát má velký QR kód pro registraci mobilem, přístupový kód a pokyny s kontakty.</p>
        {currentActionId ? <ExportButtons actionId={currentActionId} kinds={[["poster.pdf", "Plakát A4 s velkým QR kódem"], ["flyer.pdf", "Leták A4 (podrobný)"]]} /> : <p className="text-xs text-amber-600">Nejdříve vytvořte servisní akci.</p>}
      </div>
      {flyer && <FlyerDialog svjId={svjId} onClose={() => setFlyer(false)} />}
      {remind && <RemindDialog actionId={currentActionId} onClose={() => setRemind(false)} />}
    </div>
  );
}

function RemindDialog({ actionId, onClose }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/actions/${actionId}/unbooked`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))); }, [actionId]);
  const val = (a) => edits[a.id] ?? (a.contact_email || a.suggested_email || "");
  const ready = data ? data.apartments.filter((a) => val(a).includes("@")).length : 0;
  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/actions/${actionId}/remind-unbooked`, { contacts: edits });
      toast.success(`Připomínka odeslána: ${r.sent}${r.missing.length ? ` · bez e-mailu: ${r.missing.length}` : ""}`); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Připomínka nerezervovaným bytům</DialogTitle></DialogHeader>
        {!data ? <p className="text-slate-500">Načítání…</p> : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Bez rezervace: <b data-testid="unbooked-count">{data.count}</b> bytů · s e-mailem: <b data-testid="unbooked-ready">{ready}</b>. Doplňte e-maily, uloží se k bytu pro příště. E-mail obsahuje odkaz, kód i QR.</p>
            <div className="max-h-80 overflow-auto border rounded-lg divide-y">
              {data.apartments.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-3 py-1.5 text-sm" data-testid={`unbooked-row-${a.id}`}>
                  <span className="w-28 shrink-0 font-medium">V{a.entrance}/{a.number} <span className="text-xs text-slate-400">p. {a.floor}</span></span>
                  <Input value={val(a)} onChange={(e) => setEdits({ ...edits, [a.id]: e.target.value })} placeholder="e-mail obyvatele" className="h-8" data-testid={`unbooked-email-${a.id}`} />
                  {!a.contact_email && a.suggested_email && !edits[a.id] && <span className="text-[10px] text-amber-600 shrink-0">z dřívější rezervace</span>}
                </div>
              ))}
              {data.count === 0 && <p className="p-4 text-center text-slate-500">Všechny byty mají rezervaci.</p>}
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Zrušit</Button><Button onClick={send} disabled={busy || ready === 0} style={{ background: "#0A2540" }} data-testid="remind-send-confirm">Odeslat ({ready})</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlyerDialog({ svjId, onClose }) {
  const [emails, setEmails] = useState("");
  const [incl, setIncl] = useState(true);
  const [busy, setBusy] = useState(false);
  const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/svjs/${svjId}/send-flyer`, { emails: list, include_reservation_emails: incl });
      toast.success(`Leták odeslán na ${data.count} adres`); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Rozeslat leták s QR kódem</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">E-maily obyvatel (jeden na řádek nebo oddělené čárkou)</Label>
            <Textarea rows={6} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder={"jan.novak@email.cz\npetra@email.cz"} data-testid="flyer-emails" /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={incl} onCheckedChange={(c) => setIncl(!!c)} data-testid="flyer-include-res" /> Přidat e-maily z existujících rezervací</label>
          <p className="text-xs text-slate-500">Rozpoznáno adres: <b data-testid="flyer-count">{list.length}</b></p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Zrušit</Button><Button onClick={send} disabled={busy || (list.length === 0 && !incl)} style={{ background: "#0A2540" }} data-testid="flyer-send-confirm">Odeslat</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Notifications({ svjId }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/notifications?svj_id=${svjId}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [svjId]);
  if (!data) return <p className="text-slate-500 mt-4">Načítání…</p>;
  const errors = data.items.filter((i) => i.status === "error").length;
  const retry = async () => {
    try { const { data: r } = await api.post(`/notifications/retry-errors?svj_id=${svjId}`); toast.success(`Znovu zařazeno ${r.requeued} zpráv`); setTimeout(load, 2500); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-4 space-y-3">
      {!data.configured && <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">Odesílání není nakonfigurováno – zobrazuje se pouze náhled zpráv.</div>}
      {!data.sms_configured && <div className="bg-slate-50 border rounded-lg p-3 text-slate-600 text-sm" data-testid="sms-not-configured">SMS připomínky (den předem, s časem a kontaktem na servisní tým) se plánují, ale odeslání čeká na nastavení SMS brány (TopSMS.cz) správcem systému.</div>}
      {errors > 0 && <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="notifications-errors">
        <span>{errors} zpráv se nepodařilo odeslat.</span>
        <Button size="sm" variant="outline" onClick={retry} data-testid="notifications-retry">Odeslat znovu</Button>
      </div>}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500"><tr>
          {["Typ","Příjemce","Plánováno","Stav","Chyba"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>{data.items.map((n) => (
            <tr key={n.id} className="border-t"><td className="px-3 py-2">{n.type}</td><td className="px-3 py-2">{n.to}</td>
              <td className="px-3 py-2">{n.scheduled_for ? n.scheduled_for.slice(0, 16).replace("T", " ") : "ihned"}</td>
              <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded ${n.status === "sent" ? "bg-emerald-50 text-emerald-700" : n.status === "error" ? "bg-red-50 text-red-700" : "bg-slate-100"}`}>{n.status}</span></td>
              <td className="px-3 py-2 text-xs text-red-500">{n.error}</td></tr>))}
            {data.items.length === 0 && <tr><td colSpan="5" className="px-3 py-6 text-center text-slate-500">Zatím žádná oznámení.</td></tr>}
          </tbody></table>
      </div>
    </div>
  );
}

function Audit({ svjId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/svjs/${svjId}/audit`).then((r) => setRows(r.data)); }, [svjId]);
  return (
    <div className="mt-4 bg-white rounded-xl border divide-y">
      {rows.map((a) => (
        <div key={a.id} className="px-4 py-2 text-sm flex justify-between">
          <span><b>{a.action}</b> · {a.entity_type} <span className="text-slate-400">({a.actor_role})</span></span>
          <span className="text-slate-400">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="px-4 py-6 text-center text-slate-500">Zatím žádné záznamy.</div>}
    </div>
  );
}

function Empty({ text }) { return <div className="mt-4 bg-white rounded-xl border p-8 text-center text-slate-500">{text}</div>; }
